// hooks/useSalonHome.ts
import { useState, useEffect } from "react";
import { CustomerDetails, StylingStaff } from "../types/salon.types";
import { useSalonData } from "./useSaloonData";
import { useOrderManagement } from "./useOrderManagement";
import { usePaymentProcessing } from "./usePaymentProcessing";
import { useCategoryFilter } from "./useCategoryFilter";
import { useOrderFlow } from "./useOrderFlow";
import {
  completeOrder,
  fetchOutstandingBalance,
  fetchOrderDetails,
} from "./OrderService";
import { addCustomer } from "./customerService";
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from "src/assets/toastService";
import { PrintData } from "src/print-configuration/PrintConfigration";
import { roundAmount } from "../utils/formatters";
import { splitDateTime } from "src/shared/components/dateTime";

export const useSalonHome = () => {
  const [activeTab, setActiveTab] = useState<"Staffs" | "Services">("Staffs");
  const [selectedStaff, setSelectedStaff] = useState<StylingStaff | null>(null);
  const [isCustomerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [resetCategoryFlag, setResetCategoryFlag] = useState(false);
  const [outstandingBalance, setOutstandingBalance] = useState(0);
  const [isLoadingOutstanding, setIsLoadingOutstanding] = useState(false);
  const [isProcessingOrder, setIsProcessingOrder] = useState(false);
  
  const { staffMembers, products, categories, toggleRefresh, loading, error } = useSalonData();
  
  const order = useOrderManagement();
  const payment = usePaymentProcessing();
  const {
    filterProducts,
    handleCategorySelect,
    resetCategoryFilter,
    selectedCategoryId,
    filteredCategories,
  } = useCategoryFilter(products, categories, activeTab, selectedStaff);
  const { resetOrderFlow, validateOrder } = useOrderFlow(
    selectedStaff,
    payment.resetPaymentState,
    order.clearOrder,
  );
  const [print, setPrint] = useState<(PrintData & { printId: number }) | null>(
    null,
  );
  const now = new Date();

  useEffect(() => {
    if (payment.selectedCustomerId) {
      fetchOutstandingBalance(payment.selectedCustomerId)
        .then(setOutstandingBalance)
        .catch(() => setOutstandingBalance(0));
    } else {
      setOutstandingBalance(0);
    }
  }, [payment.selectedCustomerId]);

  /** Handlers */
  const handleConfirmOrder = () => {
    if (!validateOrder(order.selectedProducts)) {
      notifyWarning("Please select at least one product.");
      return;
    }
    payment.setCustomerAdd(true);
  };

  const handleCustomerSelect = (customer: CustomerDetails) => {
    payment.setCustomerName(customer.firstName);
    payment.setCustomerPhone(customer.phones[0].phone);
    payment.setSelectedCustomerId(customer._id.$oid);
    payment.setCustomerAdd(false);
    payment.setOpenProductList(true);
  };

  const handleAddCustomer = async () => {
    if (!payment.customerName || !payment.customerPhone) {
      notifyError("Please enter both name and phone number.");
      return;
    }

    try {
      const data = await addCustomer({
        firstName: payment.customerName,
        phones: [
          {
            phone: payment.customerPhone,
            isPrimary: true,
          },
        ],
      });

      payment.setSelectedCustomerId(data._id?.$oid || data._id);
      notifySuccess("Customer added successfully");
      setCustomerDialogOpen(false);
      payment.setOpenProductList(true);
    } catch (error: any) {
      const message = error.response?.data?.message || "Failed to add customer";
      notifyError(message);
    }
  };

  const handleCompletePayment = async () => {
    setIsProcessingOrder(true);
    payment.setIsProcessingPayment(true);
    
    try {
      const totals = {
        subtotal: order.calculateSubtotal(),
        total: order.calculateTotal() + payment.calculateTipsTotal(),
      };
      const received = parseFloat(payment.receivedAmount || "0");
      const isFullPayment =
        payment.paymentMethod !== "Buy on credit" && received >= totals.total;

      const { data } = await completeOrder({
        customerId: payment.selectedCustomerId!,
        staff: selectedStaff!,
        products: order.selectedProducts,
        totals,
        customer: {
          Name: payment.customerName,
          phone_number: payment.customerPhone,
        },
        payment: { method: payment.paymentMethod, received, isFullPayment },
        paymentHistory: {
          method: payment.paymentMethod,
          received,
          isFullPayment,
        },
        discount: Number(order.discount),
        tips: String(payment.tipsAmount),
        time: payment.time,
        outstandingBalance,
      });

      const updatedOSB = await fetchOutstandingBalance(
        payment.selectedCustomerId!
      );

      const orderId =
        data?.Id || data?._id || data?.data?.Id || data?.data?._id;
      let fetchedOrder: any = null;
      if (orderId) {
        fetchedOrder = await fetchOrderDetails(orderId);
      }

      const pTotal = Number(
        roundAmount(
          order.selectedProducts.reduce(
            (total, product) =>
              total + parseFloat(product.amount) * (product.quantity || 1),
            0,
          ),
        ),
      );
      const due = Number(
        pTotal - Number(order.discount) - Number(payment.receivedAmount),
      );
      const localDate =
        now.getFullYear() +
        "-" +
        String(now.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(now.getDate()).padStart(2, "0");

      const ordersList = fetchedOrder?.orders || [];
      const firstOrder = ordersList[0];
      const lastOrder = ordersList[ordersList.length - 1];
      const customerDetails = fetchedOrder?.customerDetails;
      const orderDetailsObj = fetchedOrder?.orderDetails?.[0];
      const paymentHistory = fetchedOrder?.paymentHistory || [];

      const fetchedCustomerName = customerDetails
        ? [
            customerDetails.firstName,
            customerDetails.middleName,
            customerDetails.lastName,
          ]
            .filter(Boolean)
            .join(" ")
        : "";

      const fetchedCustomerPhone = customerDetails?.phones?.[0]?.phone || "";

      const { date: orderDate, time: orderTime } = splitDateTime(
        orderDetailsObj?.orderedOn?.$date ?? "",
      );

      const formattedProducts =
        orderDetailsObj?.products && orderDetailsObj.products.length > 0
          ? orderDetailsObj.products.map((p: any) => ({
              productName: p.productName || p.name || "",
              arabicword: p.arabicName || p.arabicword || "",
              amount: Number(p.unitPrice || p.price || p.amount || 0),
              quantity: Number(p.quantity || 1),
              totalPrice: Number(
                p.lineTotal ||
                  p.itemTotal ||
                  Number(p.unitPrice || p.price || 0) *
                    Number(p.quantity || 1) ||
                  0,
              ),
            }))
          : data?.products && data.products.length > 0
            ? data.products.map((p: any) => ({
                productName: p.productName || "",
                arabicword: p.arabicword || p.arabicName || "",
                amount: Number(p.unitPrice || 0),
                quantity: Number(p.quantity || 0),
                totalPrice: Number(
                  p.unitPrice * p.quantity || p.lineTotal || p.itemTotal || 0,
                ),
              }))
            : order?.selectedProducts?.map((p: any) => ({
                productName: p.productName || "",
                arabicword: p.arabicword || p.arabicName || "",
                amount: Number(p.unitPrice || p.amount || 0),
                quantity: Number(p.quantity || 0),
                totalPrice:
                  Number(
                    p.lineTotal ||
                      p.itemTotal ||
                      Number(p.amount || 0) * Number(p.quantity || 1),
                  ) || 0,
              })) || [];

      const latestPayment =
        paymentHistory.length > 0
          ? paymentHistory[paymentHistory.length - 1]
          : null;

      const paid = latestPayment
        ? Number(latestPayment.receivedAmount || 0)
        : lastOrder?.receivedAmount !== undefined
          ? Number(lastOrder.receivedAmount)
          : Number(payment.receivedAmount || 0);

      const prevPaid =
        paymentHistory.length > 1
          ? paymentHistory
              .slice(0, -1)
              .reduce(
                (sum: number, item: any) =>
                  sum + Number(item?.receivedAmount || 0),
                0,
              )
          : 0;

      const totalPaidSoFar = paymentHistory.reduce(
        (sum: number, item: any) => sum + Number(item?.receivedAmount || 0),
        0,
      );

      const orderGrossTotal = Number(firstOrder?.orderAmount ?? totals.total);
      const orderNetTotal = Number(firstOrder?.finalAmount ?? totals.subtotal);
      const discount = Number(firstOrder?.discount ?? order.discount);

      const remainingAmountValue =
        lastOrder?.remainingAmount !== undefined
          ? Number(lastOrder.remainingAmount)
          : Math.max(0, orderNetTotal - totalPaidSoFar);

      const prevBalance = Math.max(0, orderNetTotal - prevPaid);

      const printData: PrintData & { printId: number } = {
        printId: Date.now(),
        CustomerName:
          fetchedCustomerName ||
          data?.customerName ||
          payment.customerName ||
          "",
        CustomerPhon: fetchedCustomerPhone || payment.customerPhone || "",
        OrderId:
          firstOrder?.orderId ??
          lastOrder?.orderId ??
          orderId ??
          data?.Id ??
          "",
        OrderStatus: lastOrder?.status || firstOrder?.status || "Confirm",
        discount: discount,
        Date: orderDate || localDate,
        Time: orderTime || now.toTimeString().split(" ")[0],
        Products: formattedProducts,
        Paid: paid,
        prvPaid: prevPaid,
        prvBalance: prevBalance,
        unpaid: remainingAmountValue,
        OSB: updatedOSB,
        paymentMethod: latestPayment?.paymentMethod || payment.paymentMethod,
        subtotal: orderNetTotal,
        total: orderGrossTotal,
        balanceReturned: Number(lastOrder?.balanceReturned || 0),
      };

      setPrint(printData);

      resetData();
    } catch {
      notifyError("Failed to complete payment");
    } finally {
      setIsProcessingOrder(false);
      payment.setIsProcessingPayment(false);
    }
  };

  /** resetData */
  const resetData = () => {
    setActiveTab("Staffs");
    setSelectedStaff(null);
    setCustomerDialogOpen(false);
    order.clearOrder();
    payment.resetPaymentState();
    toggleRefresh();
    setResetCategoryFlag(true);
    resetCategoryFilter();
    resetCategoryFilter();
    resetOrderFlow();
  };

  return {
    activeTab,
    setActiveTab,
    selectedStaff,
    setSelectedStaff,
    isCustomerDialogOpen,
    setCustomerDialogOpen,
    outstandingBalance,
    isLoadingOutstanding,
    staffMembers,
    products,
    categories,
    filterProducts,
    handleCategorySelect,
    resetOrderFlow,
    order,
    payment,
    handleConfirmOrder,
    handleCustomerSelect,
    handleAddCustomer,
    handleCompletePayment,
    print,
    selectedCategoryId,
    filteredCategories,
    resetCategoryFlag,
    setResetCategoryFlag,
    resetCategoryFilter,
    loading,
    error,
    isProcessingOrder,
  };
};